import { useTranslation } from '@/lib/i18n';
import type {
  VendorReviewActionEnum,
  VendorReviewLog,
} from '@/lib/api-admin/types';
import type { BadgeVariant } from '@/lib/domain';

const ACTION_BADGE: Record<VendorReviewActionEnum, BadgeVariant> = {
  ASSIGN: 'info',
  START_REVIEW: 'progress',
  APPROVE: 'success',
  REJECT: 'danger',
  REQUEST_RESUBMISSION: 'warn',
  ISSUE_CREDENTIALS: 'success',
};

const DOT_BG: Record<BadgeVariant, string> = {
  info: 'var(--status-info-fg)',
  progress: 'var(--status-progress-fg)',
  success: 'var(--status-success-fg)',
  warn: 'var(--status-warn-fg)',
  danger: 'var(--status-danger-fg)',
  neutral: 'var(--status-neutral-fg)',
};

const formatDateTime = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export interface VendorReviewLogTimelineProps {
  logs: ReadonlyArray<VendorReviewLog>;
}

export const VendorReviewLogTimeline = ({ logs }: VendorReviewLogTimelineProps) => {
  const { t } = useTranslation('vendors');

  if (logs.length === 0) {
    return (
      <p className="text-sm text-[color:var(--content-muted)]">
        {t('vendors.timeline.empty')}
      </p>
    );
  }

  return (
    <ol
      aria-label={t('vendors.timeline.label')}
      className="relative ml-3 space-y-4"
      style={{ borderLeft: '1px solid var(--content-muted)' }}
    >
      {logs.map((log) => {
        const badge = ACTION_BADGE[log.action];
        return (
          <li key={log.logId} className="relative pl-5">
            <span
              aria-hidden="true"
              className="absolute -left-[5px] top-1.5 inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: DOT_BG[badge] }}
            />
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-sm font-medium text-[color:var(--content-strong)]">
                {t(`vendors.reviewAction.${log.action}`, { defaultValue: '-' })}
              </span>
              <time
                dateTime={log.occurredAt}
                className="text-xs text-[color:var(--content-muted)]"
              >
                {formatDateTime(log.occurredAt)}
              </time>
              {log.actorName ? (
                <span className="text-xs text-[color:var(--content-muted)]">
                  · {log.actorName}
                </span>
              ) : null}
            </div>
            {log.reason ? (
              <p className="mt-1 text-sm text-[color:var(--content-default)]">{log.reason}</p>
            ) : null}
            {log.missingFields && log.missingFields.length > 0 ? (
              <ul className="mt-1 flex flex-wrap gap-1.5">
                {log.missingFields.map((field) => (
                  <li
                    key={field}
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-xs"
                    style={{
                      background: 'var(--status-warn-bg)',
                      color: 'var(--status-warn-fg)',
                    }}
                  >
                    {t(`vendors.missingFields.${field}`, { defaultValue: field })}
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
};
